import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
import { IoUmbrellaOutline } from "react-icons/io5";
import { FaHeart } from "react-icons/fa";
import { ShieldCheck } from "lucide-react";

// Import both single & bulk delete mutations
import {
  useDeleteAgencyMutation,
  useBulkDeleteAgencyMutation,
  useGetAgenciesQuery
} from "../../../../services/redux/api/components";

const Agency = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [deleteAgency, { isLoading: isDeletingSingle }] = useDeleteAgencyMutation();
  const [bulkDeleteAgency, { isLoading: isDeletingBulk }] = useBulkDeleteAgencyMutation();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteId, setDeleteId] = useState(null); // for single delete
  const [deleteMode, setDeleteMode] = useState("single"); // "single" or "multi"
  const [isOpen, setIsOpen] = useState(false); // modal open
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter & Sort states
  const [filterColumn, setFilterColumn] = useState("agencyName");
  const [filterValueInput, setFilterValueInput] = useState(""); // user typing
  const [filterValue, setFilterValue] = useState("");           // used in API
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const {
    data: agencies,
    isLoading,
    error,
    refetch, 
  } = useGetAgenciesQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  useEffect(() => {
    const options = [
      { value: "AgencyName", label: "Agency Name" },
      { value: "AgencyShortName", label: "Short Name" },
      { value: "AgencyCode", label: "Agency Code" },
      { value: "InsurerId", label: "Insurer" },
      { value: "AgencyRenewalDate", label: "Renewal Date" },
      { value: "AgencyType", label: "Agency Type" }
    ];
    setColumnOptions(options);
  }, []);

  const rowData = useMemo(() => {
    if (!agencies?.data) return [];
    return Array.isArray(agencies.data)
      ? agencies.data.map(item => ({
        ...item,
        id: item.AgencyId,
      }))
      : [];
  }, [agencies, isFilterApplied]);

  const handleFilter = () => {
    if (!filterValueInput.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setFilterValue(filterValueInput); // will trigger API fetch
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValueInput("");
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc";
    }
    setSortField(direction ? field : null);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return () => {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  const [columnDefs, setColumnDefs] = useState([]);

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Agency Name",
        field: "AgencyName",
        flex: 1,
        headerComponent: createHeaderComponent("AgencyName", "Agency Name"),
        suppressMenu: true,
      },
      {
        headerName: "Short Name",
        field: "AgencyShortName",
        flex: 1,
        headerComponent: createHeaderComponent("AgencyShortName", "Short Name"),
        suppressMenu: true,
      },
      {
        headerName: "Agency Code",
        field: "AgencyCode",
        flex: 1,
        headerComponent: createHeaderComponent("AgencyCode", "Agency Code"),
        suppressMenu: true,
      },
      {
        headerName: "Insurer",
        field: "InsurerId",
        flex: 1,
        headerComponent: createHeaderComponent("InsurerId", "Insurer"),
        suppressMenu: true,
      },
      {
        headerName: "Renewal Date",
        field: "AgencyRenewalDate",
        flex: 1,
        headerComponent: createHeaderComponent("AgencyRenewalDate", "Renewal Date"),
        suppressMenu: true,
        cellRenderer: (params) => {
          if (!params.value) return null;
          return <span className="text-sm">{params.value.slice(0, 10)}</span>;
        },
      },
      {
        headerName: "Agency Type",
        field: "AgencyType",
        flex: 1,
        headerComponent: createHeaderComponent("AgencyType", "Agency Type"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const getIcon = (type) => {
            switch (type) {
              case 'life':
                return <IoUmbrellaOutline size={20} className="text-brandRed" title="Life" />;
              case 'health':
                return <FaHeart size={20} className="text-brandRed" title="Health" />;
              case 'general':
                return <ShieldCheck size={20} className="text-brandRed" title="General" />;
              default:
                return null;
            }
          };

          return (
            <div className="flex items-center w-full h-full">
              <div className="w-[30px] h-[30px] border-2 border-brandRed text-brandRed rounded-full flex items-center justify-center">
                {getIcon(params.value)}
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => navigate(`/agency/edit/${params.data.AgencyId}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSingleDelete(params.data.AgencyId, params.data.AgencyName);
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
      },
    ];

    setColumnDefs(createColumnDefs());
  }, [sortField, sortDirection]); // you may update this dependency list as needed

  // Handles when selection checkbox changes
  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

    selectedIdsRef.current = selectedNodes.map((node) => node.data.AgencyId);
    setSelectedNames(selectedNodes.map((node) => node.data.AgencyName));

    setAllSelected(selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0);
    setDeleteMode(selectedNodes.length > 1 || allSelected ? "multi" : "single");
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one agency to delete");
      return;
    }

    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    // set selectedNames already done by onSelectionChanged
    setIsOpen(true);
  };

  // Function for opening delete modal for single-row delete (Trash2)
  const onOpenSingleDelete = (id, name) => {
    selectedIdsRef.current = [id];
    setSelectedNames([name]);
    setDeleteId(id);
    setDeleteMode("single");
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = async () => {
    try {
      if (deleteMode === "single" && deleteId) {
        const response = await deleteAgency(deleteId).unwrap();
        if (response.success) {
          toast.success(response.message || "Agency deleted successfully");
          refetch();
        } else {
          toast.error(response.message || "Failed to delete agency");
        }
      } else {
        const idsToDelete = allSelected
          ? rowData.map((x) => x.AgencyId)
          : selectedIdsRef.current;

        const response = await bulkDeleteAgency({ ids: idsToDelete }).unwrap();

        if (response.success) {
          toast.success(response.message || `${idsToDelete.length} agencies deleted successfully`);
          refetch();
        } else {
          toast.error(response.message || "Failed to delete agencies");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    } finally {
      onClose();
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil((agencies?.totalCount || 0) / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [agencies?.totalCount, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => {
        if (selectedIdsRef.current.includes(node.data.AgencyId)) {
          node.setSelected(true);
        }
      });
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Agency Master List</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/agency/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        filterValue={filterValueInput}
        setFilterValue={setFilterValueInput}
        columnOptions={columnOptions}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={agencies?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} agencies on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected agencies?`
              : `Are you sure you want to delete this agency: ${selectedNames[0]}?`
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default Agency;
